import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next';
import { Card, Popover, Typography, Tag, Modal, Input, message, Form } from 'antd'
import { DeleteOutlined, PlusOutlined, PauseOutlined, EditOutlined } from '@ant-design/icons';
import { TelegramAccountEntity } from '../../../domain/entities/TelegramAccount'
import { UserAuth } from '../../../middleware/UserContext/UserContext.middleware';
import { useTelegramAccountsStore } from '../../stores/telegramAccounts.store';

type TelegramAccountCardProps = {
  telegramAccount: TelegramAccountEntity,
  onAccountUpdated: () => void
}

const TelegramAccountCard: React.FC<TelegramAccountCardProps> = ({ telegramAccount, onAccountUpdated }) => {
  const [open, setOpen] = useState<boolean>(false)
  const [actionType, setActionType] = useState<string | null>(null)
  const [authCode, setAuthCode] = useState<string | null>(null)
  const [password, setPassword] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentUser } = UserAuth()
  const { updateStatus, remove, startAuth, finishAuth } = useTelegramAccountsStore()

  const handleActionClick = async (type: string) => {
    if (type === 'authenticate') {
      try {
        setIsProcessing(true);
        if (!currentUser) throw new Error('user-not-authenticated');
        const ok = await startAuth(currentUser, telegramAccount.uuid);
        if (ok) {
          setActionType(type);
          setOpen(true);
        }
      } catch (error: any) {
        const errorMessage = error instanceof Error ? error.message.replace(/^Error:\s*/, '') : 'unknown-error';
        message.error(t(errorMessage, { ns: 'errors' }));
      } finally {
        setIsProcessing(false);
      }
    } else {
      setActionType(type);
      setOpen(true);
    }
  }

  const handleOkClick = async () => {
    setIsProcessing(true);
    
    try {
      if (!currentUser) throw new Error('user-not-authenticated');

      if (actionType === 'delete') {
        const ok = await remove(currentUser, telegramAccount.uuid);
        if (ok) {
          hideModal();
          onAccountUpdated();
        }
      }
      else if (actionType === 'deactivate') {
        const ok = await updateStatus(currentUser, telegramAccount.uuid, false);
        if (ok) {
          hideModal();
          onAccountUpdated();
        }
      }
      else if (actionType === 'activate') {
        const ok = await updateStatus(currentUser, telegramAccount.uuid, true);
        if (ok) {
          hideModal();
          onAccountUpdated();
        }
      }
      else if (actionType === 'authenticate') {
        if (!authCode) {
          message.error(t('tg-auth-code-required', { ns: 'errors' }));
          setIsProcessing(false);
          return;
        }
        const ok = await finishAuth(currentUser, telegramAccount.uuid, authCode, password);
        if (ok) {
          hideModal();
          onAccountUpdated();
        }
      }
    } catch (error: any) {
      const errorMessage = error instanceof Error 
        ? error.message.replace(/^Error:\s*/, '') 
        : 'unknown-error';
      message.error(t(errorMessage, { ns: 'errors' }));
    } finally {
      setIsProcessing(false);
    }
  }

  const hideModal = () => {
    setOpen(false);
    setAuthCode(null);
    setPassword(null);
  }

  const brandingAction = (
    <Popover content={t('telegram-account-branding', {ns: 'standard'})} trigger="hover">
      <EditOutlined key="branding" onClick={() => navigate(`/telegram_accounts/${telegramAccount.uuid}/branding`)}/>
    </Popover>
  )

  const activeChannelCardActions: React.ReactNode[] = [
    brandingAction,
    <Popover content={t('deactivate-telegram-account', {ns: 'standard'})} trigger="hover">
      <PauseOutlined key="deactivate" onClick={() => handleActionClick('deactivate')}/>
    </Popover>,
    <Popover content={t('delete', {ns: 'standard'})} trigger="hover">
      <DeleteOutlined key="delete" onClick={() => handleActionClick('delete')}/>
    </Popover>,
  ]

  const deactivatedChannelCardActions: React.ReactNode[] = [
    <Popover content={t('activate-telegram-account', {ns: 'standard'})} trigger="hover">
      <PlusOutlined key="activate" onClick={() => handleActionClick('activate')}/>
    </Popover>,
    <Popover content={t('delete', {ns: 'standard'})} trigger="hover">
      <DeleteOutlined key="delete" onClick={() => handleActionClick('delete')}/>
    </Popover>,
  ]

  const unauthenticatedChannelCardActions: React.ReactNode[] = [
    <Popover content={t('auth-telegram-account', {ns: 'standard'})} trigger="hover">
      <PlusOutlined key="activate" onClick={() => handleActionClick('authenticate')}/>
    </Popover>,
    <Popover content={t('delete', {ns: 'standard'})} trigger="hover">
      <DeleteOutlined key="delete" onClick={() => handleActionClick('delete')}/>
    </Popover>,
  ]

  const determineActions = () => {
    if (telegramAccount.status === 'AUTHENTICATED') {
      return activeChannelCardActions
    }
    if (telegramAccount.status === 'DISABLED') {
      return deactivatedChannelCardActions
    }
    return unauthenticatedChannelCardActions
  }

  return (
    <>
      <Modal 
        title={
          actionType === 'delete' ? t('delete-telegram-account-question-title', {ns: 'standard'}) : 
          actionType === 'deactivate' ? t('deactivate-telegram-account-question-title', {ns: 'standard'}) : 
          actionType === 'activate' ? t('activate-telegram-account-question-title', {ns: 'standard'}) :
          actionType === 'authenticate' ? t('authenticate-telegram-account-question-title', {ns: 'standard'}) : ''
        }
        open={open}
        onOk={handleOkClick}
        onCancel={hideModal}
        okText={t('yes', {ns: 'standard'})}
        cancelText={t('no', {ns: 'standard'})}
        confirmLoading={isProcessing}
      >
        <Typography.Paragraph>
          {
            actionType === 'delete' ? t('delete-telegram-account-question-description', {ns: 'standard'}) : 
            actionType === 'deactivate' ? t('deactivate-telegram-account-question-description', {ns: 'standard'}) :
            actionType === 'activate' ? t('activate-telegram-account-question-description', {ns: 'standard'}) :
            actionType === 'authenticate' ? t('authenticate-telegram-account-question-description', {ns: 'standard'}) : ''
          }
        </Typography.Paragraph>
        {
          actionType === 'authenticate' && (
            <Form layout="vertical" style={{ marginTop: 16 }}>
              <Form.Item 
                label={t('tg-auth-code-label', {ns: 'standard'})} 
                required
              >
                <Input 
                  placeholder={t('tg-auth-code-placeholder', {ns: 'standard'})} 
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAuthCode(e.target.value)} 
                />
              </Form.Item>
              <Form.Item 
                label={t('tg-auth-password-label', {ns: 'standard'})}
              >
                <Input.Password 
                  placeholder={t('tg-auth-password-placeholder', {ns: 'standard'})} 
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)} 
                />
              </Form.Item>
            </Form>
          )
        }
      </Modal>
      <Card
        actions={determineActions()}
        key={telegramAccount.uuid}
        size='default'
      >
        {telegramAccount.status === 'AUTHENTICATED' && (
          <Tag color='green'>{t('tg-account-status-active', {ns: 'standard'})}</Tag>
        )}
        {telegramAccount.status === 'DISABLED' && (
          <Tag color='orange'>{t('tg-account-status-disabled', {ns: 'standard'})}</Tag>
        )}
        {(telegramAccount.status === 'UNAUTHENTICATED' || telegramAccount.status === 'UNKNOWN') && (
          <Tag color='red'>{t('tg-account-status-unauthenticated', {ns: 'standard'})}</Tag>
        )}
        <Typography.Title level={5} style={{ textAlign: 'center' }}>
          {telegramAccount.phone}
        </Typography.Title>
      </Card>
    </>
  )
}

export default TelegramAccountCard